import { useCallback, useMemo, useState } from 'react';

/**
 * Die Buchfuehrung darueber, was offen ist.
 *
 * **Gemerkt wird das Abweichende, nicht der Zustand.** Die Vorgabe -- der
 * neueste Eintrag ist offen, die uebrigen zu -- kommt aus `ids`. Waere
 * stattdessen die Menge der offenen Eintraege gespeichert, klappte ein neu
 * eingegangener Beitrag beim naechsten Laden zu, weil er in der gemerkten
 * Menge nicht vorkam.
 */
export function useTimelineOpen(ids: readonly string[], openByDefault: readonly string[] = []) {
  const [toggled, setToggled] = useState<Set<string>>(() => new Set());
  const [allOpen, setAllOpen] = useState<boolean | null>(null);

  const standard = useMemo(() => new Set(openByDefault), [openByDefault]);

  const isOpen = useCallback(
    (id: string) => {
      const base = allOpen ?? standard.has(id);
      return toggled.has(id) ? !base : base;
    },
    [allOpen, standard, toggled],
  );

  const toggle = useCallback((id: string) => {
    setToggled((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const everythingOpen = ids.length > 0 && ids.every(isOpen);

  const toggleAll = useCallback(() => {
    setAllOpen(!everythingOpen);
    setToggled(new Set());
  }, [everythingOpen]);

  return { isOpen, toggle, toggleAll, everythingOpen };
}
